// Profil-Nachbau F-25 (PF-GR-cockpit-2; SPEC cockpit-grafiken 2.3; Entwurf A entwurf/mittel/bausteine.js:429-565): der Kalender zum
// Zeitraum-Knopf des KI-Cockpits (Bausteine.tsx ZeitraumWahl), wie meinGPT auf Deutsch. Links die Vorgaben (ein Klick übernimmt),
// rechts zwei Monate (bis 667 px Breite des Cockpits einer), unten „Von“, „Bis“, „Abbrechen“, „Anwenden“. Wählbar sind heute − 365 bis heute in Wien;
// was gilt, sagt danach der Server („Angezeigt: …“). Escape, „Abbrechen“ und ein Klick daneben schließen ohne Änderung.
import React, { useEffect, useRef, useState } from 'react';
import { Check, ChevronLeft, ChevronRight } from 'lucide-react';
import { Field } from '../components';
import { zoomVon } from '../../components/Erklaerung';
import { ZEITRAUM } from './cockpitTexte';
import { datum } from './format';
import {
  datumLesen, eigenerZeitraum, heuteInWien, kalenderGrenzen, kalenderKlick, KalenderStand, kalenderTaste, monatPlus, monatsRaster, monatTitel,
  tageZwischen, tagName, VORGABEN, wahlAusTagen, wahlTage, Zeitraum, ZeitraumWahl,
} from './zeitraum';
import './kalender.css';

// PF-SL5-kalender-1: Die Darstellung richtet sich nach der Breite von .ki-cockpit (Container ki-cockpit, PF-SL5-raster-1), nicht nach
// der des Browserfensters; nur außerhalb des Cockpits gilt wie bisher SCHMAL. Bis 667 px steht der Kalender als Blatt am unteren Rand
// (kalender.css) und zeigt einen Monat, darüber zwei. Neben den Vorgaben ist der Aufklapper 894 px breit (196 + 20 + 2 × 308 + 28,
// Polster 2 × 16, Rand 2); er darf höchstens in den kleinsten rechten Rand des Inhalts (24 px) ragen, sonst rollt der Inhalt waagrecht.
// Passt er so nicht, stehen die Vorgaben darüber („mittel“, 678 px breit).
const SCHMAL = '(max-width: 699px)';
const BLATT_BIS = 667, AUFKLAPPER = 894, RAND = 24;
export type KalenderDarstellung = 'breit' | 'mittel' | 'blatt';
export const kalenderDarstellung = (breite: number): KalenderDarstellung =>
  breite <= BLATT_BIS ? 'blatt' : breite + RAND < AUFKLAPPER ? 'mittel' : 'breit';
const cockpitVon = (element: Element | null) => element?.closest<HTMLElement>('.ki-cockpit') ?? null;
const tagWahl = (tag: string) => `.ki-kalender-tag[data-tag="${tag}"]`;

export default function ZeitraumKalender({ id, wahl, angezeigt, knopf, onWahl, onSchliessen }: { id: string; wahl: ZeitraumWahl;
  angezeigt?: Zeitraum; knopf: React.RefObject<HTMLButtonElement>; onWahl: (wahl: ZeitraumWahl) => void; onSchliessen: (zurueck: boolean) => void }) {
  // Beim Öffnen einmal: heute in Wien, Beginn und Ende aus „Angezeigt“ (sonst aus der Wahl), rechter Monat = Monat des Endes.
  const [start] = useState(() => { const heute = heuteInWien(new Date()); return { heute, ...(angezeigt ?? wahlTage(wahl, heute)) }; });
  const { heute } = start;
  const { min, max } = kalenderGrenzen(heute);
  const [stand, setStand] = useState<KalenderStand>({ anfang: start.von, ende: start.bis, wartet: false });
  const [blick, setBlick] = useState(start.bis.slice(0, 7));
  const [fokus, setFokus] = useState(start.bis);
  const [vonText, setVonText] = useState(datum(start.von));
  const [bisText, setBisText] = useState(datum(start.bis));
  const [fehler, setFehler] = useState<{ von?: string; bis?: string }>({});
  const [darstellung, setDarstellung] = useState<KalenderDarstellung>(() => {
    const cockpit = cockpitVon(knopf.current);
    return cockpit ? kalenderDarstellung(cockpit.getBoundingClientRect().width / zoomVon(cockpit)) : window.matchMedia(SCHMAL).matches ? 'blatt' : 'breit';
  });
  const schmal = darstellung === 'blatt';
  const kalender = useRef<HTMLDivElement>(null);
  const vonFeld = useRef<HTMLInputElement>(null), bisFeld = useRef<HTMLInputElement>(null);
  // Nach dem Zeichnen erhält das erste vorhandene Element dieser Liste den Fokus (beim Öffnen die gedrückte Vorgabe, sonst der
  // fokussierbare Tag; nach Tasten und Blättern der neue Tag bzw. der Blätterknopf).
  const fokusNach = useRef<string[] | null>(['.ki-kalender-vorgabe[aria-pressed="true"]', '.ki-kalender-tag[tabindex="0"]']);

  useEffect(() => {
    const ziele = fokusNach.current;
    if (!ziele) return;
    fokusNach.current = null;
    for (const ziel of ziele) {
      const element = kalender.current?.querySelector<HTMLElement>(ziel);
      if (element) { element.focus(); return; }
    }
  });
  useEffect(() => {
    const cockpit = cockpitVon(knopf.current);
    if (cockpit) {
      const beobachter = new ResizeObserver(([eintrag]) => setDarstellung(kalenderDarstellung(eintrag.contentRect.width)));
      beobachter.observe(cockpit);
      return () => beobachter.disconnect();
    }
    const liste = window.matchMedia(SCHMAL);
    const aendern = () => setDarstellung(liste.matches ? 'blatt' : 'breit');
    liste.addEventListener('change', aendern);
    return () => liste.removeEventListener('change', aendern);
  }, [knopf]);
  // Ein Klick daneben (außerhalb von Kalender und Knopf) schließt ohne Änderung.
  useEffect(() => {
    const aussen = (event: PointerEvent) => {
      const ziel = event.target as Node | null;
      if (ziel && !kalender.current?.contains(ziel) && !knopf.current?.contains(ziel)) onSchliessen(false);
    };
    document.addEventListener('pointerdown', aussen, true);
    return () => document.removeEventListener('pointerdown', aussen, true);
  }, [knopf, onSchliessen]);

  const monate = schmal ? [blick] : [monatPlus(blick, -1), blick];
  const darfZurueck = monatPlus(monate[0], -1) >= min.slice(0, 7);
  const darfVor = blick < max.slice(0, 7);
  const sichtbar = monate.flatMap(monat => monatsRaster(monat).flat()).filter((tag): tag is string => tag !== null && tag >= min && tag <= max);
  const tabTag = sichtbar.includes(fokus) ? fokus : sichtbar[0];

  const { anfang, ende, wartet } = stand;
  const unten = ende !== null && ende < anfang ? ende : anfang, oben = ende !== null && ende > anfang ? ende : anfang;
  const imBereich = (tag: string) => tag === anfang || (ende !== null && tag >= unten && tag <= oben);
  const klassen = (tag: string) => ['ki-kalender-tag', tag === anfang && 'anfang', tag === ende && 'ende',
    ende !== null && tag > unten && tag < oben && 'im-bereich', tag === heute && 'heute'].filter(Boolean).join(' ');
  const zusaetze = (tag: string) => [tag === anfang ? ZEITRAUM.beginn : '', tag === ende ? ZEITRAUM.ende : '', tag === heute ? ZEITRAUM.heute : '']
    .filter(teil => teil !== '');
  const laenge = ende !== null ? tageZwischen(unten, oben) : 1;

  const klick = (tag: string) => {
    const neu = kalenderKlick(stand, tag);
    setStand(neu);
    setVonText(datum(neu.anfang)); setBisText(neu.ende ? datum(neu.ende) : '');
    setFehler({}); setFokus(tag);
    fokusNach.current = [tagWahl(tag)];
  };
  const blaettern = (n: number, knopfWahl: string) => {
    setBlick(vorher => monatPlus(vorher, n));
    fokusNach.current = [`${knopfWahl}:not([hidden])`, '.ki-kalender-tag[tabindex="0"]'];
  };
  // Beim Verlassen setzt ein echter Tag Beginn bzw. Ende, und nur das verlassene Feld wird geprüft; ein leeres Feld meldet erst
  // „Anwenden“ (wie bisher in ZeitraumWahl).
  const verlassen = (feld: 'von' | 'bis', text: string) => {
    if (!text.trim()) return;
    const tag = datumLesen(text);
    if (tag) {
      setStand(vorher => feld === 'von' ? { ...vorher, anfang: tag, wartet: false } : { ...vorher, ende: tag, wartet: false });
      setBlick(tag.slice(0, 7) > max.slice(0, 7) ? max.slice(0, 7) : tag.slice(0, 7));
      setFokus(tag);
    }
    const e = eigenerZeitraum(vonText, bisText, heute);
    setFehler(v => ({ ...v, [feld]: e.ok ? undefined : e.fehler[feld] }));
  };
  // Ohne „Bis“ (nach dem ersten Klick) gilt der Tag aus „Von“ als Beginn und Ende.
  const anwenden = (event: React.FormEvent) => {
    event.preventDefault();
    const e = eigenerZeitraum(vonText, bisText.trim() ? bisText : vonText, heute);
    if (!e.ok) { setFehler(e.fehler); (e.fehler.von ? vonFeld : bisFeld).current?.focus(); return; }
    onWahl(wahlAusTagen(e.von, e.bis, heute));
  };
  const taste = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape') { event.preventDefault(); onSchliessen(true); return; }
    const tag = (event.target as HTMLElement).closest<HTMLButtonElement>('.ki-kalender-tag')?.dataset.tag;
    if (!tag) return;
    const neu = kalenderTaste(tag, event.key, { min, max });
    if (!neu) return;
    event.preventDefault();
    // Der neue Tag wird sichtbar: davor rückt er in den ersten, danach in den letzten Monat.
    const monat = neu.slice(0, 7), erster = monate[0];
    let neuerBlick = blick;
    if (monat < erster) neuerBlick = schmal ? monat : monatPlus(monat, 1);
    else if (monat > blick) neuerBlick = monat;
    setBlick(neuerBlick > max.slice(0, 7) ? max.slice(0, 7) : neuerBlick);
    setFokus(neu);
    fokusNach.current = [tagWahl(neu)];
  };
  // Geht der Fokus per Tab auf ein Element außerhalb von Kalender und Knopf, schließt der Kalender ohne Änderung.
  const fokusWeg = (event: React.FocusEvent<HTMLDivElement>) => {
    const neu = event.relatedTarget as Node | null;
    if (neu && !event.currentTarget.contains(neu) && !knopf.current?.contains(neu)) onSchliessen(false);
  };

  return <div ref={kalender} id={id} className="ki-kalender" data-darstellung={darstellung} role="dialog" aria-label={ZEITRAUM.dialog} onKeyDown={taste}
    onBlur={fokusWeg}>
    <ul className="ki-kalender-vorgaben" aria-label={ZEITRAUM.vorgabenListe}>
      {VORGABEN.map(v => <li key={v}><button type="button" className="ki-kalender-vorgabe" aria-pressed={wahl.art === 'VORGABE' && wahl.vorgabe === v}
        onClick={() => onWahl({ art: 'VORGABE', vorgabe: v })}><span>{ZEITRAUM.vorgaben[v]}</span><Check size={16} aria-hidden="true" /></button></li>)}
    </ul>
    <div className="ki-kalender-monate">{monate.map((monat, i) => <div key={monat} className="ki-kalender-monat">
      <div className="ki-kalender-monat-kopf">
        <button type="button" className="ki-kalender-blaettern" aria-label={ZEITRAUM.vorheriger} hidden={!(i === 0 && darfZurueck)}
          onClick={() => blaettern(-1, `.ki-kalender-blaettern[aria-label="${ZEITRAUM.vorheriger}"]`)}><ChevronLeft size={18} aria-hidden="true" /></button>
        <h3>{monatTitel(monat)}</h3>
        <button type="button" className="ki-kalender-blaettern" aria-label={ZEITRAUM.naechster} hidden={!(i === monate.length - 1 && darfVor)}
          onClick={() => blaettern(1, `.ki-kalender-blaettern[aria-label="${ZEITRAUM.naechster}"]`)}><ChevronRight size={18} aria-hidden="true" /></button>
      </div>
      <table role="grid" aria-label={monatTitel(monat)}>
        <thead><tr>{ZEITRAUM.wochentage.map((w, j) => <th key={w} scope="col" abbr={ZEITRAUM.wochentageLang[j]}>{w}</th>)}</tr></thead>
        <tbody>{monatsRaster(monat).map((woche, w) => <tr key={w}>{woche.map((tag, j) => <td key={j}>{tag && <button type="button" className={klassen(tag)}
          data-tag={tag} tabIndex={tag === tabTag ? 0 : -1} aria-label={tagName(tag, zusaetze(tag))} aria-pressed={imBereich(tag)}
          disabled={tag < min || tag > max} onClick={() => klick(tag)}>{Number(tag.slice(8))}</button>}</td>)}</tr>)}</tbody>
      </table>
    </div>)}</div>
    <form className="ki-kalender-fuss" onSubmit={anwenden} noValidate>
      <Field label={ZEITRAUM.von} fehler={fehler.von}><input ref={vonFeld} type="text" inputMode="numeric" autoComplete="off" placeholder={ZEITRAUM.platzhalter} value={vonText}
        onChange={event => setVonText(event.target.value)} onBlur={event => verlassen('von', event.target.value)} /></Field>
      <Field label={ZEITRAUM.bis} fehler={fehler.bis}><input ref={bisFeld} type="text" inputMode="numeric" autoComplete="off" placeholder={ZEITRAUM.platzhalter} value={bisText}
        onChange={event => setBisText(event.target.value)} onBlur={event => verlassen('bis', event.target.value)} /></Field>
      <p className="ki-kalender-laenge" aria-live="polite">{wartet ? ZEITRAUM.endeWaehlen : ZEITRAUM.tage(laenge)}</p>
      <div className="ki-kalender-aktionen">
        <button type="button" className="adm-button" onClick={() => onSchliessen(true)}>{ZEITRAUM.abbrechen}</button>
        <button type="submit" className="adm-button adm-primary" onMouseDown={event => event.preventDefault()}>{ZEITRAUM.anwenden}</button>
      </div>
    </form>
  </div>;
}
